/**
 * NewThread (/forums/new)
 *
 * Premium thread-creation form.
 *   - Category dropdown (fetched from /api/categories)
 *   - Title input (3-200 chars)
 *   - Body textarea
 *   - Tag input (comma-separated, trimmed + deduped)
 *
 * On success, posts to POST /api/threads and redirects to /forums/thread/:id.
 */
import * as React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, PenSquare } from "lucide-react";
import { api, extractApiError } from "../../lib/api";
import type { CategoryNode, Thread } from "../../lib/types";
import { Button } from "../../components/ui/Button";
import { Card, CardBody, CardHeader } from "../../components/ui/Card";
import { Input } from "../../components/ui/Input";
import { Select } from "../../components/ui/Select";

export default function NewThread() {
  const navigate = useNavigate();
  const [categories, setCategories] = React.useState<CategoryNode[]>([]);
  const [title, setTitle] = React.useState("");
  const [categoryId, setCategoryId] = React.useState("");
  const [body, setBody] = React.useState("");
  const [tagsCsv, setTagsCsv] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await api.get<CategoryNode[]>("/categories?tree=true");
        setCategories(r.data);
        // Default to first selectable option: childless root, or first child
        const first =
          r.data.find((c) => c.children.length === 0) ??
          r.data.flatMap((c) => c.children)[0];
        if (first) setCategoryId(first.id);
      } catch (err) {
        setError(extractApiError(err));
      }
    })();
  }, []);

  const remaining = 200 - title.length;
  const titleTooShort = title.trim().length > 0 && title.trim().length < 3;
  const canSubmit =
    title.trim().length >= 3 &&
    title.trim().length <= 200 &&
    body.trim().length > 0 &&
    !!categoryId &&
    !loading;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const tags = Array.from(
        new Set(
          tagsCsv
            .split(",")
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean),
        ),
      );
      const r = await api.post<Thread>("/threads", {
        title: title.trim(),
        body: body.trim(),
        category_id: categoryId,
        tags,
      });
      navigate(`/forums/thread/${r.data.id}`);
    } catch (err) {
      setError(extractApiError(err));
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-8 py-8 md:py-12">
      <Link
        to="/forums"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink-muted hover:text-ink mb-4"
        data-testid="new-thread-back"
      >
        <ArrowLeft className="h-4 w-4" /> Back to forums
      </Link>

      <Card elevated>
        <CardHeader>
          <div>
            <h1 className="text-2xl md:text-3xl font-black tracking-tight inline-flex items-center gap-2">
              <PenSquare className="h-6 w-6 text-brand" aria-hidden /> Start a thread
            </h1>
            <p className="text-ink-muted text-sm mt-1">
              Ask a question, share a repair, or post a want-to-buy. Replies can
              include a product listing, so another member can sell you the part
              in-line.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={submit} className="flex flex-col gap-5" data-testid="new-thread-form">
            <Select
              label="Category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              placeholder="Pick a category"
              required
              data-testid="new-thread-category"
            >
              {categories.map((cat) =>
                cat.children.length === 0 ? (
                  // Childless root (e.g. General) — valid posting destination
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ) : (
                  // Parent with children — non-selectable group header
                  <optgroup key={cat.id} label={cat.name.toUpperCase()}>
                    {cat.children.map((child) => (
                      <option key={child.id} value={child.id}>
                        {child.name}
                      </option>
                    ))}
                  </optgroup>
                ),
              )}
            </Select>

            <Input
              label="Title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Yamaha F250 fuel starvation at WOT — any fixes?"
              maxLength={200}
              error={titleTooShort ? "At least 3 characters." : undefined}
              hint={!titleTooShort ? `${remaining} characters remaining` : undefined}
              data-testid="new-thread-title"
            />

            <div>
              <label
                htmlFor="new-thread-body-input"
                className="mb-1.5 block font-semibold text-ink text-sm"
              >
                Body
              </label>
              <textarea
                id="new-thread-body-input"
                required
                rows={8}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Describe the issue, the boat/motor, what you've tried so far…"
                data-testid="new-thread-body"
                className="w-full min-h-[180px] rounded-xl border-2 border-surface-border bg-white px-3 py-3 text-base text-ink placeholder:text-ink-subtle outline-none transition-colors focus:border-ink"
              />
              <p className="mt-1.5 text-xs text-ink-muted">
                Plain text. Links auto-detected on render.
              </p>
            </div>

            <Input
              label="Tags (optional)"
              value={tagsCsv}
              onChange={(e) => setTagsCsv(e.target.value)}
              placeholder="yamaha, fuel, f250"
              hint="Comma-separated. Tags help others find your thread."
              data-testid="new-thread-tags"
            />

            {error && (
              <div
                role="alert"
                data-testid="new-thread-error"
                className="rounded-xl border-2 border-brand bg-brand-softer text-brand-ink px-3 py-2 text-sm font-semibold"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row items-stretch gap-3 pt-2">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={loading}
                disabled={!canSubmit}
                fullWidth
                leadingIcon={<PenSquare className="h-5 w-5" />}
                data-testid="new-thread-submit"
              >
                Post thread
              </Button>
              <Link to="/forums" className="w-full sm:w-auto">
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  fullWidth
                  data-testid="new-thread-cancel"
                >
                  Cancel
                </Button>
              </Link>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
